import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, AlertOctagon, ArrowUp, ArrowDown, Check, X, PencilLine } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/dateUtils";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

function ReferenceList({ title, items, onAdd, onToggleActive, testPrefix }) {
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    await onAdd(newName.trim());
    setNewName("");
    setAdding(false);
  };

  return (
    <div>
      <h2 className="font-semibold text-amtm-text mb-2">{title}</h2>
      <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border mb-3" data-testid={`${testPrefix}-list`}>
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between p-3" data-testid={`${testPrefix}-row-${item.id}`}>
            <span className={item.active ? "text-amtm-text" : "text-amtm-textMuted line-through"}>{item.name}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-amtm-textMuted">{item.active ? "Active" : "Archived"}</span>
              <Switch checked={item.active} onCheckedChange={(v) => onToggleActive(item.id, v)} data-testid={`${testPrefix}-active-toggle-${item.id}`} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Add new" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid={`${testPrefix}-new-input`} />
        <Button onClick={handleAdd} disabled={adding} data-testid={`${testPrefix}-add-button`}>
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

/** Category/Form/Unit management: rename inline, reorder with up/down arrows
 * (simpler and more reliable on the budget touchscreens staff use than drag),
 * usage count next to each option, and deactivate-only (never hard-delete) so
 * historical items keep their label even after an option is retired. */
function ManagedOptionList({ title, items, onAdd, onRename, onToggleActive, onMove, testPrefix }) {
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    await onAdd(newName.trim());
    setNewName("");
    setAdding(false);
  };

  const startRename = (item) => {
    setRenamingId(item.id);
    setRenameValue(item.name);
  };

  const saveRename = async () => {
    if (renameValue.trim()) await onRename(renamingId, renameValue.trim());
    setRenamingId(null);
  };

  return (
    <div>
      <h2 className="font-semibold text-amtm-text mb-2">{title}</h2>
      <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border mb-3" data-testid={`${testPrefix}-list`}>
        {items.map((item, idx) => (
          <div key={item.id} className="flex items-center justify-between p-3 gap-2" data-testid={`${testPrefix}-row-${item.id}`}>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => onMove(item.id, "up")} data-testid={`${testPrefix}-move-up-${item.id}`}>
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" disabled={idx === items.length - 1} onClick={() => onMove(item.id, "down")} data-testid={`${testPrefix}-move-down-${item.id}`}>
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
            </div>

            {renamingId === item.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus data-testid={`${testPrefix}-rename-input-${item.id}`} />
                <Button size="icon" onClick={saveRename} data-testid={`${testPrefix}-rename-save-${item.id}`}><Check className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="outline" onClick={() => setRenamingId(null)} data-testid={`${testPrefix}-rename-cancel-${item.id}`}><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <button type="button" onClick={() => startRename(item)} className="flex items-center gap-1.5 flex-1 text-left" data-testid={`${testPrefix}-rename-trigger-${item.id}`}>
                <span className={item.active ? "text-amtm-text" : "text-amtm-textMuted line-through"}>{item.name}</span>
                <PencilLine className="h-3 w-3 text-amtm-textMuted" />
              </button>
            )}

            <span className="text-xs text-amtm-textMuted shrink-0" data-testid={`${testPrefix}-usage-${item.id}`}>{item.usage_count} in use</span>
            <Switch checked={item.active} onCheckedChange={(v) => onToggleActive(item.id, v)} data-testid={`${testPrefix}-active-toggle-${item.id}`} />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Add new" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid={`${testPrefix}-new-input`} />
        <Button onClick={handleAdd} disabled={adding} data-testid={`${testPrefix}-add-button`}>
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export default function InventorySettings() {
  const [locations, setLocations] = useState(null);
  const [categories, setCategories] = useState(null);
  const [forms, setForms] = useState(null);
  const [units, setUnits] = useState(null);
  const [optionsAuditLog, setOptionsAuditLog] = useState([]);
  const [resetInfo, setResetInfo] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const { toast } = useToast();

  const fetchAll = useCallback(async () => {
    const [locRes, catRes, formRes, unitRes, auditRes, resetRes] = await Promise.all([
      api.get("/inventory/locations", { params: { include_archived: true } }),
      api.get("/inventory/categories", { params: { include_archived: true } }),
      api.get("/inventory/forms", { params: { include_archived: true } }),
      api.get("/inventory/units", { params: { include_archived: true } }),
      api.get("/inventory/audit-log"),
      api.get("/inventory/reset-check"),
    ]);
    setLocations(locRes.data);
    setCategories(catRes.data);
    setForms(formRes.data);
    setUnits(unitRes.data);
    setOptionsAuditLog(auditRes.data);
    setResetInfo(resetRes.data);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleAddLocation = async (name) => {
    try {
      await api.post("/inventory/locations", { name });
      fetchAll();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not add drawer", description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const handleToggleLocation = async (id, active) => {
    await api.patch(`/inventory/locations/${id}`, { active });
    fetchAll();
  };

  const makeHandlers = (endpoint) => ({
    onAdd: async (name) => {
      try {
        await api.post(endpoint, { name });
        fetchAll();
      } catch (err) {
        toast({ variant: "destructive", title: "Could not add option", description: formatApiErrorDetail(err.response?.data?.detail) });
      }
    },
    onRename: async (id, name) => {
      try {
        await api.patch(`${endpoint}/${id}`, { name });
        fetchAll();
      } catch (err) {
        toast({ variant: "destructive", title: "Could not rename", description: formatApiErrorDetail(err.response?.data?.detail) });
      }
    },
    onToggleActive: async (id, active) => {
      await api.patch(`${endpoint}/${id}`, { active });
      fetchAll();
    },
    onMove: async (id, direction) => {
      await api.post(`${endpoint}/${id}/move`, null, { params: { direction } });
      fetchAll();
    },
  });

  const categoryHandlers = makeHandlers("/inventory/categories");
  const formHandlers = makeHandlers("/inventory/forms");
  const unitHandlers = makeHandlers("/inventory/units");

  const handleReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/inventory/reset");
      toast({ title: "Medical Inventory reset", description: `${data.items_deleted} items, ${data.batches_deleted} batches, ${data.movements_deleted} movements removed.` });
      setResetDialogOpen(false);
      setResetConfirmText("");
      fetchAll();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not reset", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setResetting(false);
    }
  };

  const loaded = locations !== null && categories !== null && forms !== null && units !== null;

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-2" data-testid="inventory-settings-title">Drawers, Categories, Forms & Units</h1>
        <InventorySubNav />

        {!loaded ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : (
          <div className="space-y-8">
            <ReferenceList title="Drawers" items={locations} onAdd={handleAddLocation} onToggleActive={handleToggleLocation} testPrefix="settings-drawer" />
            <ManagedOptionList title="Categories" items={categories} {...categoryHandlers} testPrefix="settings-category" />
            <ManagedOptionList title="Forms" items={forms} {...formHandlers} testPrefix="settings-form" />
            <ManagedOptionList title="Units" items={units} {...unitHandlers} testPrefix="settings-unit" />

            <div>
              <h2 className="font-semibold text-amtm-text mb-2">Recent changes</h2>
              <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border max-h-64 overflow-y-auto" data-testid="settings-audit-log">
                {optionsAuditLog.length === 0 ? (
                  <p className="text-sm text-amtm-textMuted p-3">No changes recorded yet.</p>
                ) : (
                  optionsAuditLog.map((entry) => (
                    <div key={entry.id} className="p-3 text-sm" data-testid={`settings-audit-row-${entry.id}`}>
                      <p className="text-amtm-text capitalize">
                        {entry.entity_type} <strong>{entry.entity_label}</strong> {entry.action}
                        {entry.changes.map((c) => c.field === "name" ? ` (${c.from} → ${c.to})` : "").join("")}
                      </p>
                      <p className="text-xs text-amtm-textMuted">{formatDate(entry.changed_at)} by {entry.changed_by_name}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="border border-amtm-error/30 rounded-xl p-4 bg-amtm-error/5" data-testid="inventory-danger-zone">
              <div className="flex items-center gap-2 mb-2">
                <AlertOctagon className="h-5 w-5 text-amtm-error" />
                <h2 className="font-semibold text-amtm-text">Danger Zone</h2>
              </div>
              <p className="text-sm text-amtm-textMuted mb-3" data-testid="inventory-reset-counts">
                Currently {resetInfo?.counts.items ?? 0} items, {resetInfo?.counts.batches ?? 0} batches, {resetInfo?.counts.movements ?? 0} movements recorded.
              </p>
              {resetInfo && !resetInfo.can_reset && (
                <p className="text-sm text-amtm-warning mb-3" data-testid="inventory-reset-blocked-reason">
                  Cannot reset — real activity already recorded: {Object.entries(resetInfo.blocking_movement_counts).map(([k, v]) => `${v} ${k}`).join(", ")}.
                </p>
              )}
              <Button
                variant="outline"
                disabled={!resetInfo?.can_reset || (resetInfo?.counts.items ?? 0) === 0}
                onClick={() => setResetDialogOpen(true)}
                data-testid="inventory-reset-open-dialog-button"
                className="text-amtm-error border-amtm-error/40 hover:bg-amtm-error/10 disabled:opacity-40"
              >
                Reset Medical Inventory
              </Button>
            </div>
          </div>
        )}

        <Dialog open={resetDialogOpen} onOpenChange={(open) => { setResetDialogOpen(open); if (!open) setResetConfirmText(""); }}>
          <DialogContent data-testid="inventory-reset-dialog">
            <DialogHeader>
              <DialogTitle>Reset Medical Inventory?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-amtm-textMuted">
              This permanently deletes all {resetInfo?.counts.items ?? 0} items, {resetInfo?.counts.batches ?? 0} batches and {resetInfo?.counts.movements ?? 0} movements.
              Drawers, categories, forms and units are kept. This cannot be undone. Type <strong>RESET</strong> to confirm.
            </p>
            <Input value={resetConfirmText} onChange={(e) => setResetConfirmText(e.target.value)} data-testid="inventory-reset-confirm-input" placeholder="RESET" />
            <Button
              onClick={handleReset}
              disabled={resetConfirmText !== "RESET" || resetting}
              data-testid="inventory-reset-confirm-button"
              className="bg-amtm-error text-white hover:bg-amtm-error/90"
            >
              {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete Everything
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
